import { useEffect, useState } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { IntegrityEventType } from 'generated/graphql';
import {
  CloseIntegrityEvent,
  OpenIntegrityEvent,
  ReportIntegrityEvent,
} from 'hooks/useIntegrityMonitoring';
import { useVisionIntegrityDetection } from 'hooks/useVisionIntegrityDetection';

type HookProps = {
  room: Room | null;
  enabled: boolean;
  openEvent: OpenIntegrityEvent;
  closeEvent: CloseIntegrityEvent;
  reportEvent: ReportIntegrityEvent;
};

// AI-interview wrapper around the source-agnostic vision detection core:
// derives the analysed MediaStream from LiveKit's local camera track (no
// extra getUserMedia), follows mutes and device switches, and reports the
// switch itself so reviewers see it (a swap to a virtual camera is invisible
// otherwise). The device label lands in metadata.
export function useAiInterviewVisionIntegrityDetection({
  room,
  enabled,
  openEvent,
  closeEvent,
  reportEvent,
}: HookProps): void {
  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    if (!room || !enabled) {
      setStream(null);
      return;
    }
    const activeRoom = room;
    let disposed = false;

    const deriveStream = () => {
      const publication = activeRoom.localParticipant.getTrackPublication(
        Track.Source.Camera,
      );
      const mediaStreamTrack =
        publication?.isMuted === false
          ? publication.track?.mediaStreamTrack
          : undefined;
      setStream(
        mediaStreamTrack ? new MediaStream([mediaStreamTrack]) : null,
      );
    };

    const onTrackChange = (publication: { source: Track.Source }) => {
      if (publication.source !== Track.Source.Camera) return;
      deriveStream();
    };
    const onLocalTrackMuteChange = (
      publication: { source: Track.Source },
      participant: { isLocal: boolean },
    ) => {
      if (!participant.isLocal) return;
      if (publication.source !== Track.Source.Camera) return;
      deriveStream();
    };

    let lastVideoDeviceId: string | null =
      activeRoom.getActiveDevice('videoinput') ?? null;

    const reportDeviceSwitch = async (deviceId: string) => {
      let deviceLabel: string | null = null;
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        deviceLabel =
          devices.find((device) => device.deviceId === deviceId)?.label ||
          null;
      } catch {
        // The label is best-effort metadata; the switch is reported anyway.
      }
      if (disposed) return;
      reportEvent(IntegrityEventType.CameraDeviceSwitched, { deviceLabel });
    };

    // A camera device switch replaces the MediaStreamTrack in place, without
    // any publish/unpublish events — re-derive the stream here so detection
    // follows the new source.
    const onActiveDeviceChanged = (kind: MediaDeviceKind, deviceId: string) => {
      if (kind !== 'videoinput') return;
      const previousDeviceId = lastVideoDeviceId;
      lastVideoDeviceId = deviceId;
      deriveStream();
      // The first activation is the lobby-selected camera, not a switch.
      if (previousDeviceId === null || previousDeviceId === deviceId) return;
      void reportDeviceSwitch(deviceId);
    };

    activeRoom.on(RoomEvent.LocalTrackPublished, onTrackChange);
    activeRoom.on(RoomEvent.LocalTrackUnpublished, onTrackChange);
    activeRoom.on(RoomEvent.TrackMuted, onLocalTrackMuteChange);
    activeRoom.on(RoomEvent.TrackUnmuted, onLocalTrackMuteChange);
    activeRoom.on(RoomEvent.ActiveDeviceChanged, onActiveDeviceChanged);
    deriveStream();

    return () => {
      disposed = true;
      activeRoom.off(RoomEvent.LocalTrackPublished, onTrackChange);
      activeRoom.off(RoomEvent.LocalTrackUnpublished, onTrackChange);
      activeRoom.off(RoomEvent.TrackMuted, onLocalTrackMuteChange);
      activeRoom.off(RoomEvent.TrackUnmuted, onLocalTrackMuteChange);
      activeRoom.off(RoomEvent.ActiveDeviceChanged, onActiveDeviceChanged);
      setStream(null);
    };
  }, [room, enabled, reportEvent]);

  useVisionIntegrityDetection({
    enabled,
    stream,
    openEvent,
    closeEvent,
    reportEvent,
  });
}
